import { useState, type FormEvent } from 'react';
import { useSuggestDishes } from '@/api/dishes/dishes';
import type { DishPick } from '@/components/dish-picker';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/field';
import { kcal } from '@/lib/utils';

/** Matches the API's bounds, so an out-of-range ask never becomes a 400. */
const MIN_KCAL = 1;
const MAX_KCAL = 5000;

/**
 * "I have about this many calories — what should I eat?"
 *
 * Suggestions are one serving each: the question is which dish fits, and a
 * fractional plate is not an answer anyone acts on. Picking a row hands the
 * caller the same `DishPick` the search box does, so a suggestion can be
 * planned or logged by whatever screen is asking.
 *
 * The list is deliberately not filtered to near-misses — the dish table is
 * small, and an empty answer is less useful than five with their gaps shown.
 */
export function DishSuggester({
  onPick,
  busy = false,
  defaultKcal,
}: {
  onPick: (pick: DishPick) => void;
  busy?: boolean;
  /** Prefills the box — usually what is left of the day's budget. */
  defaultKcal?: number | null;
}) {
  const [input, setInput] = useState(defaultKcal == null ? '' : String(Math.round(defaultKcal)));
  // Only what the user actually submitted: typing "5" on the way to "500"
  // should not fire a request, let alone show suggestions for 5 kcal.
  const [target, setTarget] = useState<number | null>(null);

  const asked = Number(input);
  const validAsk = Number.isFinite(asked) && asked >= MIN_KCAL && asked <= MAX_KCAL;

  const suggestions = useSuggestDishes(
    { kcal: target ?? 0 },
    { query: { enabled: target != null } },
  );

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (validAsk) setTarget(asked);
  };

  return (
    <div className="space-y-3">
      <form onSubmit={submit} className="flex items-center gap-2">
        <Input
          type="number"
          inputMode="numeric"
          min={MIN_KCAL}
          max={MAX_KCAL}
          // Whole kcal, and every one of them valid: a coarser step would make
          // the browser reject round asks like 500 for not landing on the grid.
          step={1}
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="เช่น 500"
          aria-label="อยากได้ประมาณกี่แคลอรี"
        />
        <Button type="submit" disabled={!validAsk} className="shrink-0">
          แนะนำเมนู
        </Button>
      </form>

      {target == null && (
        <p className="text-sm text-ink-muted">
          กรอกจำนวนแคลอรีที่อยากได้ แล้วเราจะเลือกเมนูที่ใกล้เคียงที่สุด 5 อันดับ
        </p>
      )}

      {suggestions.isPending && target != null && (
        <p className="text-sm text-ink-muted">กำลังเลือกเมนู…</p>
      )}

      {suggestions.data?.length === 0 && (
        <p className="text-sm text-ink-muted">ยังไม่มีเมนูในระบบให้แนะนำ</p>
      )}

      {suggestions.data != null && suggestions.data.length > 0 && (
        <ol className="divide-y divide-border-subtle">
          {suggestions.data.map((dish, index) => (
            <li key={dish.id}>
              <button
                type="button"
                disabled={busy}
                onClick={() => onPick({ dishId: dish.id, qty: 1 })}
                // The row reads as rank, name, gap and kcal all run together;
                // the label is what a screen reader announces instead.
                aria-label={`เลือก ${dish.nameTh}`}
                className="flex w-full items-center gap-3 py-2.5 text-left disabled:opacity-50"
              >
                <span className="w-5 shrink-0 text-sm tabular-nums text-ink-muted">
                  {index + 1}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{dish.nameTh}</span>
                  <span className="block text-xs text-ink-muted">
                    {dish.servingLabel} · {gapLabel(dish.diffKcal)}
                    {!dish.verified && ' · ประมาณการ'}
                  </span>
                </span>
                <span className="shrink-0 text-sm tabular-nums">
                  {kcal(dish.kcalPerServing)} kcal
                </span>
              </button>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

/**
 * How far the dish lands from the ask. Rounded first, so a gap that displays as
 * zero is never labelled "over" — the number on the row is what the user is
 * comparing against.
 */
function gapLabel(diffKcal: number): string {
  const gap = Math.round(diffKcal);
  if (gap === 0) return 'พอดีเป๊ะ';
  return gap > 0 ? `เกิน ${kcal(gap)} kcal` : `น้อยกว่า ${kcal(-gap)} kcal`;
}
